import Vue from "vue";
import Vuex from "vuex";

Vue.use(Vuex);

export const enum ACTIONS {
  SET_CURRENCY = "setCurrency",
  SET_LOGIN = "setLogin",
  SET_CURRENCIES = "setCurrencies",
  SET_POS = "setPos",
  SET_CLIENT = "setClient",
  SET_ACCOUNT_CHAIN = "setAccountChain",
  CLEAR_SESSION = "clearSession"
}

export type StateUser = {
  id?: number;
  name?: string;
  email?: string;
  logo?: string;
  /** Emails de la cuenta y sus ancestros, del mas cercano al mas lejano. */
  accountChain?: Array<string>;
  isLogged?: boolean;
  token?: string;
  /** La sesion se abrio desde el sitio de un socio, no con contrasena. */
  viaSso?: boolean;
  canManageUsers?: boolean;
  canCreateContent?: boolean;
  canReserve?: boolean;
  canEmmit?: boolean;
  canCancel?: boolean;
};

export type StateCurrency = {
  code: string;
  createdAt: string;
  id: number;
  markup: number;
  usdPrice: number;
};

export type ClientConfig = {
  name: string;
  logoPath?: string;
  bannerPath?: string;
  colorScheme?: {
    primary: string;
    secondary: string;
  };
  customImages?: {
    [key: string]: string;
  };
};

export type StoreState = {
  user: StateUser;
  currentCurrency: StateCurrency;
  currencies: Array<StateCurrency>;
  currentClient: ClientConfig | null;
  pos: any;
};


export default new Vuex.Store({
  state: {
    user: JSON.parse(localStorage.getItem("cardinal-user") || "{}"),    
    currentCurrency: JSON.parse(
      localStorage.getItem("cardinal-currency") || "{}"
    ),
    pos: JSON.parse(localStorage.getItem("cardinal-pos") || "{}"),
    currencies: [] as Array<StateCurrency>,
    currentClient: JSON.parse(
      localStorage.getItem("cardinal-client") || "null"
    ) as ClientConfig | null
  },
  mutations: {
    SET_LOGIN(state, payload: StateUser) {
      state.user = payload || {};
      // La accion normaliza el logout a `{}`, que es truthy: sin mirar las
      // claves, la rama de limpieza nunca corria y quedaba un `cardinal-user`
      // viejo en localStorage.
      if (payload && Object.keys(payload).length) {
        localStorage.setItem("cardinal-user", JSON.stringify(payload));
      } else {
        localStorage.removeItem("cardinal-user");
        localStorage.removeItem("token");
        // Idem `clearSession`: la marca la escribe el backoffice y sobrevive a
        // la sesion, asi que hay que sacarla en todos los caminos de salida.
        localStorage.removeItem("ssoSession");
      }
    },
    SET_CURRENCY(state, payload: StateCurrency) {
      state.currentCurrency = payload;
      localStorage.setItem("cardinal-currency", JSON.stringify(payload));
    },
    SET_POS(state, payload: { language: string }) {
      state.pos = payload;
      localStorage.setItem("cardinal-pos", JSON.stringify(payload));
    },
    SET_CURRENCIES(state, payload: Array<StateCurrency>) {
      state.currencies = payload;
    },
    SET_USER_PERMS(state, payload: Partial<StateUser>) {
      state.user = { ...state.user, ...payload };
      // Se puede guardar en el localstorage si es necesario
      localStorage.setItem("cardinal-user", JSON.stringify(state.user));
    },
    SET_ACCOUNT_CHAIN(state, payload: Array<string>) {
      state.user = { ...state.user, accountChain: payload };
      localStorage.setItem("cardinal-user", JSON.stringify(state.user));
    },
    SET_CLIENT(state, payload: ClientConfig | null) {
      state.currentClient = payload;
      if (payload) {
        localStorage.setItem("cardinal-client", JSON.stringify(payload));
      } else {
        localStorage.removeItem("cardinal-client");
      }
    },
  },
  actions: {
    setCurrencies({ commit }, payload: Array<StateCurrency>) {
      commit("SET_CURRENCIES", payload);
    },
    setCurrency({ commit }, payload: StateCurrency) {
      commit("SET_CURRENCY", payload);
    },
    setPos({ commit }, payload: StateCurrency) {
      commit("SET_POS", payload);
    },
    setLogin({ commit }, payload: StateUser) {
      commit("SET_LOGIN", payload ? payload : {});

      if (payload) {
        // Una sesion que ya trae sus permisos los trajo de la API, para el
        // usuario que efectivamente entro: mandan sobre cualquier cache.
        //
        // El fallback de abajo lee `apollo-cache-persist`, que escribe el panel
        // React y sobrevive en el navegador a la sesion que lo dejo. Aplicarlo
        // igual borraba los permisos de un ingreso por SSO en cada recarga,
        // porque `main.ts` dispara `validateToken` y eso vuelve a pasar por
        // aca. Queda solo para el camino que no los trae: el salto desde el
        // panel de agencias.
        if (payload.canReserve !== undefined) return;

        const apolloCache = JSON.parse(
          localStorage.getItem("apollo-cache-persist") || "{}"
        );
        const userInfo = apolloCache["$ROOT_QUERY.userInfo"];

        if (userInfo) {
          const perms = {
            canManageUsers: userInfo.canManageUsers,
            canCreateContent: userInfo.canCreateContent,
            canReserve: userInfo.canReserve,
            canEmmit: userInfo.canEmmit,
            canCancel: userInfo.canCancel
          };
          commit("SET_USER_PERMS", perms);
        }
      }
    },
    /**
     * Borra la sesion persistida sin tocar el estado reactivo. Se usa cuando ya
     * estamos navegando fuera de la app: vaciar el store ahi hace que Vue
     * repinte todo sin marca (logo y color de Cardinal, banner y secciones
     * ocultas que reaparecen) justo antes de que el navegador cambie de pagina.
     *
     * No commitea a proposito: las claves de localStorage viven en este modulo,
     * pero el estado en memoria se va con la navegacion.
     */
    clearSession() {
      localStorage.removeItem("cardinal-user");
      localStorage.removeItem("token");
      // La escribe el backoffice al entrar por SSO, y las dos apps comparten
      // origen. Si no se borra, el proximo que use este navegador cierra sesion
      // y termina en la pantalla del socio sin haber entrado por ahi.
      localStorage.removeItem("ssoSession");
    },
    setAccountChain({ commit }, payload: Array<string>) {
      commit("SET_ACCOUNT_CHAIN", payload);
    },
    setClient({ commit }, payload: ClientConfig | null) {
      commit("SET_CLIENT", payload);
    }
  },
  modules: {}
});
